import ApiService from '../api.service'
import getCurrentCompanyId from '../company.service'

const base = () => `/v1/companies/${getCurrentCompanyId()}/management`

const ManagementResource = {
  // Configuração do módulo (uma por empresa)
  getModule: () => ApiService.get(`${base()}_module`),
  updateModule: (params: {
    title?: string
    enabled?: boolean
    default_window_days?: number
    favorite_area_id?: number | null
    show_favorite_overview?: boolean
  }) => ApiService.put(`${base()}_module`, params),

  // Árvore de áreas
  getAreas: () => ApiService.get(`${base()}_areas`),
  createArea: (params: { name: string; parent_id?: number | null; position?: number }) =>
    ApiService.post(`${base()}_areas`, params),
  updateArea: (areaId: number, params: { name?: string; position?: number; parent_id?: number | null }) =>
    ApiService.put(`${base()}_areas/${areaId}`, params),
  deleteArea: (areaId: number) => ApiService.delete(`${base()}_areas/${areaId}`),

  // Indicadores agregados de uma área (subárvore) num período.
  // refresh=true ignora o cache do backend e recalcula.
  getAreaIndicators: (
    areaId: number,
    params: { start_date: string; end_date: string; refresh?: boolean },
  ) => ApiService.get(`${base()}_areas/${areaId}/indicators`, params),

  // Série diária de tempo rodando x tempo parado (paradas de interrupção) da área.
  getDailyRuntime: (
    areaId: number,
    params: { start_date: string; end_date: string; refresh?: boolean },
  ) => ApiService.get(`${base()}_areas/${areaId}/daily_runtime`, params),

  // Consolidação mensal de energia da área (ou de um sensor). Cache no SQL por sensor/mês.
  getMonthlyConsolidation: (
    areaId: number,
    month: string,
    opts: { sensorId?: number | null; metrics?: string[]; refresh?: boolean } = {},
  ) =>
    ApiService.get(`${base()}_areas/${areaId}/monthly_consolidation`, {
      month,
      ...(opts.sensorId ? { sensor_id: opts.sensorId } : {}),
      ...(opts.metrics?.length ? { metrics: opts.metrics.join(',') } : {}),
      ...(opts.refresh ? { refresh: true } : {}),
    }),

  // Previsto (forecast) — grade do ano por área + sensores
  getForecasts: (areaId: number, year: number) =>
    ApiService.get(`${base()}_areas/${areaId}/forecasts`, { year }),
  saveForecasts: (areaId: number, payload: Record<string, unknown>) =>
    ApiService.put(`${base()}_areas/${areaId}/forecasts`, payload),
  // Série previsto x realizado (consumo total) mês a mês + acumulado
  getForecastVsActual: (
    areaId: number,
    year: number,
    opts: { sensorId?: number | null; refresh?: boolean } = {},
  ) =>
    ApiService.get(`${base()}_areas/${areaId}/forecast_vs_actual`, {
      year,
      ...(opts.sensorId ? { sensor_id: opts.sensorId } : {}),
      ...(opts.refresh ? { refresh: true } : {}),
    }),
  // Desdobramento por sensor (previsto x realizado) de um mês.
  getSensorBreakdown: (areaId: number, month: string, opts: { refresh?: boolean } = {}) =>
    ApiService.get(`${base()}_areas/${areaId}/sensor_breakdown`, {
      month,
      ...(opts.refresh ? { refresh: true } : {}),
    }),

  // Sensores de uma área + configuração de indicadores
  attachSensor: (areaId: number, params: { sensor_id: number; indicators: string[]; position?: number }) =>
    ApiService.post(`${base()}_areas/${areaId}/sensors`, params),
  updateAreaSensor: (areaId: number, areaSensorId: number, params: { indicators?: string[]; position?: number }) =>
    ApiService.put(`${base()}_areas/${areaId}/sensors/${areaSensorId}`, params),
  detachSensor: (areaId: number, areaSensorId: number) =>
    ApiService.delete(`${base()}_areas/${areaId}/sensors/${areaSensorId}`),

  // Tarifas de energia (CRUD)
  listTariffs: () => ApiService.get(`/v1/companies/${getCurrentCompanyId()}/energy_tariffs`),
  createTariff: (params: Record<string, unknown>) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/energy_tariffs`, params),
  updateTariff: (id: number, params: Record<string, unknown>) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/energy_tariffs/${id}`, params),
  deleteTariff: (id: number) => ApiService.delete(`/v1/companies/${getCurrentCompanyId()}/energy_tariffs/${id}`),

  // Metas (limites ideais) de alarme por tipo — meta × realizado no relatório.
  listAlarmTargets: () => ApiService.get(`/v1/companies/${getCurrentCompanyId()}/management_alarm_targets`),
  upsertAlarmTargets: (targets: { alarm_type: string; target_count: number }[]) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/management_alarm_targets/upsert`, { targets }),

  // Relatório gerencial em PDF (a API só monta o PDF com os dados enviados).
  generateReport: (payload: Record<string, unknown>) => ApiService.postBlob('/v1/management/report', payload),
}

export default ManagementResource
